import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import {Pagination} from 'lib/Listings';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {FrozenEntityChipData} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  PlateMaterialInput,
  PlateMap,
  ReagentAssignmentGroup,
  SchematizedDisplayLayerMetadata,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {plateMapHasAnyMaterialInputs} from 'samples/plate-maps/PlateMapUtil';
import {BaseDetailStructuredTableRowMetadata} from 'samples/plate-maps/transfer-table/BaseDetailTransferStructuredTableAdapter';
import {
  DetailViewTransferTableColumnKey,
  SharedTransferTableColumnKey,
  PlateRecordViewTransferTableColumnKey,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {getPlateRecordViewIndicesByColumnKey} from 'samples/plate-maps/transfer-table/FillPlateTransferTableUtil';
import PlateRecordDetailStructuredTableAdapter from 'samples/plate-maps/transfer-table/PlateRecordDetailStructuredTableAdapter';
import {getPlateRecordViewRowsContents} from 'samples/plate-maps/transfer-table/PlateRecordDetailTableUtil';
import {GRAY, GRAY_LIGHTER} from 'ui-library/Colors';
import ResultsPager from 'ui-library/ResultsPager';

export interface PlateRecordDetailTableProps {
  /** Information about how to page through the selected wells. */
  pagination: Pagination;
  /** The plate map being filled. */
  plateMap: PlateMap;
  /** In a procedure context, a list of plate-used material inputs. */
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /** The set of reagent schemas in the plate. */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected. */
  selectedWellIndices: Set<number>;
  /** A function to update the pagination offset. */
  setPaginationOffset: (offset: number) => void;
  /** A map from (bioentity, container) API ID to frozen name and schema icon of that model. */
  frozenEntityChipData: {[id: string]: FrozenEntityChipData};
  /** The list of plate data parameter fields. Used to display tag values in table. */
  plateDataParameterMetadata: Array<SchematizedDisplayLayerMetadata>;
  /** The list of well schema fields. Used to display tag values in table. */
  wellSchemaFieldMetadata: Array<SchematizedDisplayLayerMetadata>;
  testOrderInfoById: TestOrderInfoById;
}

export default function PlateRecordDetailTable({
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  setPaginationOffset,
  frozenEntityChipData,
  plateDataParameterMetadata,
  wellSchemaFieldMetadata,
  testOrderInfoById,
}: PlateRecordDetailTableProps): JSX.Element {
  const cid = 'plate-record-structured-table';
  const adapter = PlateRecordDetailStructuredTableAdapter.getAdapter();

  const showMaterialInputColumn = plateMaterialInputs != null || plateMapHasAnyMaterialInputs(plateMap);
  const indicesByColumnKey = getPlateRecordViewIndicesByColumnKey(showMaterialInputColumn);
  const rowsContentsByColumnKey = getPlateRecordViewRowsContents({
    pagination,
    plateMap,
    plateMaterialInputs,
    reagentGroupsById,
    selectedWellIndices,
    showMaterialInputColumn,
    plateDataParameterMetadata,
    wellSchemaFieldMetadata,
  });
  const tableMetadata = {
    plateMaterialInputs: plateMaterialInputs ?? [],
    showMaterialInputColumn,
    frozenEntityChipData,
    reagentGroupsById,
    plateDataParameterMetadata,
    wellSchemaFieldMetadata,
    testOrderInfoById,
  };

  if (rowsContentsByColumnKey.length === 0) {
    return <div />;
  }

  function buildStructuredTableRows(): Array<StructuredTableRow<BaseDetailStructuredTableRowMetadata>> {
    const rows: Array<StructuredTableRow<BaseDetailStructuredTableRowMetadata>> = [];
    for (const [index, rowContents] of rowsContentsByColumnKey.entries()) {
      const row = produce(
        adapter.getBlankRow(
          tableMetadata,
          index,
          adapter.getAllSupportedColumns({
            entryId: '',
            metadata: tableMetadata,
            schemas: [],
            selectorsById: {},
            structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
          })
        ),
        (draft) => {
          draft.metadata.wellIndex = rowContents.wellIndex;
          draft.metadata.isContent = rowContents.isExistingContent;
          draft.metadata.isEmpty = rowContents.isEmpty;
          draft.metadata.containerOrContainableId = rowContents.containerOrContainableId;
          draft.metadata.containableId = rowContents.containableId;
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.WELL]] =
            rowContents[DetailViewTransferTableColumnKey.WELL];
          draft.cells[indicesByColumnKey[PlateRecordViewTransferTableColumnKey.EXPERIMENTAL_ROLE]] =
            rowContents[PlateRecordViewTransferTableColumnKey.EXPERIMENTAL_ROLE];
          draft.cells[indicesByColumnKey[PlateRecordViewTransferTableColumnKey.SOURCE_CONTAINER]] =
            rowContents[PlateRecordViewTransferTableColumnKey.SOURCE_CONTAINER];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_ICON]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_ICON];
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.SOURCE]] =
            rowContents[DetailViewTransferTableColumnKey.SOURCE];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_CONCENTRATION]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_CONCENTRATION];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.TRANSFER_QUANTITY]] =
            rowContents[SharedTransferTableColumnKey.TRANSFER_QUANTITY];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT]] =
            rowContents[SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.FINAL_CONCENTRATION]] =
            rowContents[SharedTransferTableColumnKey.FINAL_CONCENTRATION];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT]] =
            rowContents[SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT];
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.FINAL_QUANTITY]] =
            rowContents[DetailViewTransferTableColumnKey.FINAL_QUANTITY];
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT]] =
            rowContents[DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT];
          if (rowContents.hasOwnProperty(SharedTransferTableColumnKey.MATERIAL_INPUT)) {
            draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.MATERIAL_INPUT]] =
              rowContents[SharedTransferTableColumnKey.MATERIAL_INPUT]!;
          }
          const highestConstantIndex = Math.max(...Object.values(indicesByColumnKey));
          const extraColumns = [
            ...Object.values(rowContents.schemaTagColumns),
            ...Object.values(rowContents.dataParameterColumns),
          ];
          for (const [index, column] of Object.values(extraColumns).entries()) {
            draft.cells[highestConstantIndex + index + 1] = column;
          }
        }
      );
      rows.push(row);
    }
    return rows;
  }

  const table = adapter.createTable({
    name: '',
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  table.rows = buildStructuredTableRows();

  return (
    <>
      <div className={css(styles.tableWrapper)}>
        <StructuredTableOutsideNotebook
          canRename={false}
          cid={cid}
          hideColumnHeaderIcons={true}
          includeUnsubmittedStatus={false}
          isCollapsible={false}
          structuredTable={{...table, cid}}
          suppressTitleBar={true}
          hideFooter={true}
          updateHandler={() => {}}
          enableFormulas={false}
          preserveMergedCellsInPaste={true}
        />
      </div>
      <div className={css(styles.paginationWrapper)}>
        <ResultsPager
          allowFastForwardAndBackward={true}
          isLoading={false}
          setOffset={setPaginationOffset}
          pagination={pagination}
          resultDescription="well"
        />
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    marginLeft: -TableLayoutConstants.TABLE_PADDING,
  },
  paginationWrapper: {
    backgroundColor: GRAY_LIGHTER,
    border: `1px solid ${GRAY}`,
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    // Overlap the internal padding on the TableEditable
    marginTop: '-21px',
    marginRight: TableLayoutConstants.TABLE_PADDING,
    padding: '12px',
  },
});
